import { NotFoundContent } from '@platform/auth';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@shared/ui';
import { EventCalendar } from '@shared/ui/components/EventCalendar/EventCalendar';
import type {
  CalendarEvent,
  CalendarView,
  DropProposal,
  ISODate,
} from '@shared/ui/components/EventCalendar/types';
import {
  addDays,
  mondayOf,
  monthGridDates,
  todayISO,
} from '@shared/ui/components/EventCalendar/utils';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { apiSlice } from '../../store/api/apiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import AdminAppointmentActionsDialog from '../admin-appointments/AdminAppointmentActionsDialog';
import AdminAppointmentBookingDialog from '../admin-appointments/AdminAppointmentBookingDialog';
import {
  adminAppointmentErrorMessage,
  isAdminAppointmentTerminalRejection,
} from '../admin-appointments/adminAppointmentErrors';
import {
  adminAppointmentsApi,
  useLazyGetAdminAppointmentOperationQuery,
  useLazyGetAdminAppointmentQuery,
  useLazyGetAdminAppointmentSchedulingContextQuery,
  useRescheduleAdminAppointmentMutation,
} from '../admin-appointments/api/adminAppointmentsApi';
import { useStableIdempotencyKey } from '../admin-appointments/useStableIdempotencyKey';
import type { AppointmentKind } from '../appointments/types';
import { useGetAdminCalendarViewQuery } from './api/adminCalendarViewApi';
import type { CalendarViewModel } from './calendarViewTransforms';
import { calendarLocalTimeToInstant, toAdminCalendarViewModel } from './calendarViewTransforms';
import { useIncrementalCalendarEvents } from './useIncrementalCalendarEvents';

interface AdminCalendarViewProps {
  personId: string;
  memberPersonId?: string;
  displayName?: string;
  initialCursor?: ISODate;
  weekStartsOn?: 0 | 1;
  navigation?: ReactNode;
}

type PendingDrop = DropProposal;

export function AdminCalendarView({
  personId,
  memberPersonId,
  displayName,
  initialCursor = todayISO(),
  weekStartsOn = 1,
  navigation,
}: AdminCalendarViewProps) {
  const dispatch = useAppDispatch();
  const [view, setView] = useState<CalendarView>('week');
  const [cursor, setCursor] = useState<ISODate>(initialCursor);
  const [bookingKind, setBookingKind] = useState<AppointmentKind | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
  const [pendingPhase, setPendingPhase] = useState<'confirmation' | 'operation'>('confirmation');
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(null);
  const [pendingAppointmentId, setPendingAppointmentId] = useState<string | null>(null);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [nearTermWarning, setNearTermWarning] = useState(false);
  const [nearTermConfirmed, setNearTermConfirmed] = useState(false);
  const submissionInFlight = useRef(false);
  const adminPrincipalId = useAppSelector((state) => {
    const principalId = state.auth.user?.id;
    if (!principalId) throw new Error('authenticated admin principal is required');
    return principalId;
  });
  const rescheduleIdempotency = useStableIdempotencyKey('admin-reschedule', adminPrincipalId);
  const [getAdminAppointment] = useLazyGetAdminAppointmentQuery();
  const [getAdminSchedulingContext] = useLazyGetAdminAppointmentSchedulingContextQuery();
  const [getAdminOperation] = useLazyGetAdminAppointmentOperationQuery();
  const [rescheduleAdminAppointment] = useRescheduleAdminAppointmentMutation();
  const isSchedule = view === 'schedule';
  const invalidateAppointmentCaches = (appointmentId: string) => {
    dispatch(
      adminAppointmentsApi.util.invalidateTags([
        'Appointments',
        'Sessions',
        'CalendarSettings',
        { type: 'Appointments', id: appointmentId },
        { type: 'Appointments', id: `${appointmentId}:history` },
      ])
    );
  };

  // Week/Month load a single ≤42-day window. The List view pages incrementally
  // (the API caps each request at 42 days), so its query is skipped here.
  const range = useMemo(() => viewRange(view, cursor, weekStartsOn), [view, cursor, weekStartsOn]);
  const {
    data: cachedData,
    currentData,
    error: queryError,
    isFetching: isQueryFetching,
    isLoading: isQueryLoading,
    refetch,
  } = useGetAdminCalendarViewQuery(
    isSchedule ? skipToken : { personId, start: range.start, end: range.end }
  );
  const queryData = currentData ?? cachedData;
  const displayData = queryData?.person_id === personId ? queryData : undefined;

  const incremental = useIncrementalCalendarEvents({
    personId,
    anchor: cursor,
    enabled: isSchedule,
  });

  const model = useMemo<CalendarViewModel | null>(() => {
    if (isSchedule) {
      // Keep the calendar (and its toolbar) mounted while a List page loads —
      // ScheduleListView shows its own loading/empty state. Only fall back to
      // the error alert when a failure left us with nothing to show.
      if (incremental.error && incremental.events.length === 0) return null;
      return {
        events: incremental.events,
        hours: [],
        availableRanges: {},
        chemistryAvailableRanges: {},
        warnings: incremental.warnings,
      };
    }
    return displayData ? toAdminCalendarViewModel(displayData) : null;
  }, [isSchedule, incremental.error, incremental.events, incremental.warnings, displayData]);
  const calendarEvents = useMemo(
    () =>
      model && pendingDrop
        ? applyPendingDrop(model.events, pendingDrop, pendingPhase)
        : model?.events,
    [model, pendingDrop, pendingPhase]
  );

  const canDragEvent = (event: CalendarEvent) =>
    view === 'week' && pendingDrop === null && isDraggableNativeAppointment(event);

  const handleEventDrop = (proposal: DropProposal) => {
    const event = model?.events.find((candidate) => candidate.id === proposal.eventId);
    if (!event || !isDraggableNativeAppointment(event)) return false;
    try {
      calendarLocalTimeToInstant(proposal.toDate, proposal.toStart, displayData?.timezone ?? 'UTC');
    } catch {
      setRescheduleError('That local time does not exist because of a daylight-saving change.');
      return false;
    }
    setRescheduleError(null);
    setNearTermWarning(false);
    setNearTermConfirmed(false);
    setPendingPhase('confirmation');
    setPendingDrop(proposal);
    return true;
  };

  const abortPendingDrop = () => {
    if (pendingPhase !== 'confirmation') return;
    setPendingDrop(null);
    setPendingAppointmentId(null);
    setNearTermWarning(false);
    setNearTermConfirmed(false);
  };

  const confirmPendingDrop = async () => {
    if (
      !pendingDrop ||
      pendingDrop.conflict ||
      !displayData ||
      submissionInFlight.current ||
      (nearTermWarning && !nearTermConfirmed)
    )
      return;
    const event = model?.events.find((candidate) => candidate.id === pendingDrop.eventId);
    const appointmentId = nativeAppointmentId(event);
    if (!appointmentId) return;
    submissionInFlight.current = true;
    setPendingPhase('operation');
    setRescheduleError(null);
    let operationPending = false;
    try {
      const appointment = await getAdminAppointment(appointmentId, false).unwrap();
      if (!appointment.action_capabilities.reschedule.allowed) {
        throw new Error('This appointment can no longer be rescheduled.');
      }
      const schedulingContext = await getAdminSchedulingContext(
        {
          kind: appointment.kind,
          coach_person_id: appointment.expected_identities.coach.person_id,
          member_person_id: appointment.expected_identities.member.person_id,
          viewer_timezone: displayData.timezone,
          visible_weeks: 1,
          intent: 'reschedule',
          appointment_id: appointment.id,
        },
        false
      ).unwrap();
      if (
        Array.isArray(schedulingContext.warnings) &&
        schedulingContext.warnings.includes('near_term_coaching_reschedule') &&
        !nearTermConfirmed
      ) {
        setNearTermWarning(true);
        setPendingPhase('confirmation');
        return;
      }
      let expectedSeriesMutationVersion: number | null = null;
      if (appointment.series_id) {
        if (appointment.series_mutation_version === undefined) {
          throw new Error('The appointment series version is unavailable. Refresh and try again.');
        }
        expectedSeriesMutationVersion = appointment.series_mutation_version;
      }
      const commandInputs = {
        contract_version: 'appointment-admin-reschedule.v1' as const,
        coach_person_id: appointment.expected_identities.coach.person_id,
        member_person_id: appointment.expected_identities.member.person_id,
        expected_version: appointment.version,
        expected_series_mutation_version: expectedSeriesMutationVersion,
        start_at: calendarLocalTimeToInstant(
          pendingDrop.toDate,
          pendingDrop.toStart,
          displayData.timezone
        ),
        viewer_timezone: displayData.timezone,
        scope: 'appointment' as const,
      };
      const result = await rescheduleAdminAppointment({
        appointmentId,
        command: {
          ...commandInputs,
          idempotency_key: rescheduleIdempotency.keyFor({
            ...commandInputs,
            appointment_id: appointmentId,
          }),
        },
      }).unwrap();
      operationPending = result.status === 'pending';
      if (operationPending) {
        setPendingOperationId(result.operation_id);
        setPendingAppointmentId(appointmentId);
      }
      const terminal = operationPending
        ? await awaitAdminOperation(result.operation_id, getAdminOperation)
        : result;
      operationPending = false;
      setPendingOperationId(null);
      setPendingAppointmentId(null);
      rescheduleIdempotency.markTerminal();
      invalidateAppointmentCaches(appointmentId);
      if (terminal.status === 'rejected') {
        throw new Error(terminal.rejection?.message ?? 'The appointment reschedule was rejected.');
      }
      setPendingDrop(null);
      setNearTermWarning(false);
      setNearTermConfirmed(false);
      void refetch();
    } catch (error) {
      if (isAdminAppointmentTerminalRejection(error)) {
        rescheduleIdempotency.markTerminal();
      }
      setRescheduleError(
        error instanceof Error ? error.message : adminAppointmentErrorMessage(error)
      );
      void refetch();
      if (operationPending) return;
      setPendingOperationId(null);
      setPendingDrop(null);
      setPendingAppointmentId(null);
    } finally {
      submissionInFlight.current = false;
    }
  };

  const retryPendingOperation = async () => {
    if (!pendingOperationId || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setRescheduleError(null);
    let terminalReached = false;
    try {
      const terminal = await awaitAdminOperation(pendingOperationId, getAdminOperation);
      terminalReached = true;
      rescheduleIdempotency.markTerminal();
      if (pendingAppointmentId) invalidateAppointmentCaches(pendingAppointmentId);
      if (terminal.status === 'rejected') {
        throw new Error(terminal.rejection?.message ?? 'The appointment reschedule was rejected.');
      }
      setPendingOperationId(null);
      setPendingDrop(null);
      setPendingAppointmentId(null);
      void refetch();
    } catch (error) {
      if (terminalReached) {
        setPendingOperationId(null);
        setPendingDrop(null);
        setPendingAppointmentId(null);
      }
      setRescheduleError(
        error instanceof Error
          ? error.message
          : 'Could not check the appointment reschedule. Please try again.'
      );
      void refetch();
    } finally {
      submissionInFlight.current = false;
    }
  };

  const error = isSchedule ? incremental.error : queryError;
  // List view renders its own internal spinner, so it never uses the full-card
  // loading state (which would unmount the toolbar on every reload).
  const showInitialLoading = isSchedule
    ? incremental.isLoadingInitial && !displayData
    : (isQueryLoading || isQueryFetching) && !displayData;

  if (isTargetNotFoundError(error)) {
    return <NotFoundContent />;
  }

  if (error && !model) {
    return <Alert severity="error">Could not load admin calendar view. Please try again.</Alert>;
  }

  if (showInitialLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography>Loading calendar availability…</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const title = displayName ? `${displayName}'s availability` : 'Admin calendar view';
  // In List view the main query is skipped, so don't show its stale range.
  const subtitle =
    !isSchedule && displayData
      ? `${displayData.timezone} · ${displayData.range.start} to ${displayData.range.end}`
      : 'Coach availability and selected connected calendars are shown together.';

  function handleEventClick(event: CalendarEvent) {
    const appointmentId = event.meta?.appointmentId;
    if (typeof appointmentId === 'string') setSelectedAppointmentId(appointmentId);
  }

  function handleCalendarChangeComplete() {
    dispatch(apiSlice.util.invalidateTags(['Appointments', 'Sessions', 'CalendarSettings']));
  }

  return (
    <Box
      data-testid="admin-calendar-view"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {navigation}
      {rescheduleError && <Alert severity="error">{rescheduleError}</Alert>}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="gray"
            size="small"
            disabled={!memberPersonId}
            onClick={() => setBookingKind('chemistry')}
          >
            Book chemistry call
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!memberPersonId}
            onClick={() => setBookingKind('coaching')}
          >
            Book biweekly sessions
          </Button>
        </Box>
      </Box>

      {model && (
        <>
          {model.warnings.map((warning) => (
            <Alert key={warningKey(warning)} severity="warning">
              {warning.message}
            </Alert>
          ))}

          <Box
            sx={{
              height: 680,
              border: '1px solid #e8e5e0',
              borderRadius: 2,
              overflow: 'hidden',
              background: '#fafaf8',
            }}
          >
            <EventCalendar
              events={calendarEvents ?? model.events}
              members={[]}
              view={view}
              cursor={cursor}
              onViewChange={setView}
              onCursorChange={setCursor}
              hours={model.hours}
              weekStartsOn={weekStartsOn}
              canDragEvent={canDragEvent}
              onEventDrop={handleEventDrop}
              snapMinutes={15}
              availableRanges={model.availableRanges}
              chemistryAvailableRanges={model.chemistryAvailableRanges}
              showKindHatches={view === 'week'}
              allowedViews={['week', 'month', 'schedule']}
              onReachEnd={incremental.loadFuture}
              onReachStart={incremental.loadPast}
              isLoadingInitial={incremental.isLoadingInitial}
              isLoadingFuture={incremental.isLoadingFuture}
              isLoadingPast={incremental.isLoadingPast}
              pageError={incremental.pageError}
              onEventClick={handleEventClick}
            />
          </Box>
        </>
      )}
      {bookingKind && memberPersonId && (
        <AdminAppointmentBookingDialog
          open
          kind={bookingKind}
          coachPersonId={personId}
          coachDisplayName={displayName}
          memberPersonId={memberPersonId}
          viewerTimezone={displayData?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
          onClose={() => setBookingKind(null)}
          onComplete={handleCalendarChangeComplete}
        />
      )}
      {selectedAppointmentId && (
        <AdminAppointmentActionsDialog
          open
          appointmentId={selectedAppointmentId}
          viewerTimezone={displayData?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
          onClose={() => setSelectedAppointmentId(null)}
          onComplete={handleCalendarChangeComplete}
        />
      )}
      <Dialog open={pendingDrop !== null} onClose={abortPendingDrop}>
        <DialogTitle>Confirm reschedule</DialogTitle>
        <DialogContent>
          {pendingDrop && (
            <>
              <Typography>
                {pendingPhase === 'operation'
                  ? 'Reschedule pending…'
                  : `Move this appointment to ${formatDropTime(pendingDrop, displayData?.timezone)}?`}
              </Typography>
              {pendingDrop.conflict && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This time overlaps {pendingDrop.conflict.conflictingTitle}. Conflict override is
                  unavailable.
                </Alert>
              )}
              {nearTermWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This session is less than 48 hours away. Confirm immediate participant notice
                  before saving.
                </Alert>
              )}
              {nearTermWarning && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={nearTermConfirmed}
                      disabled={pendingPhase === 'operation'}
                      onChange={(event) => setNearTermConfirmed(event.target.checked)}
                    />
                  }
                  label="I understand participants may need immediate notice"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={abortPendingDrop} disabled={pendingPhase === 'operation'}>
            Abort
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              void (pendingPhase === 'operation' ? retryPendingOperation() : confirmPendingDrop())
            }
            disabled={
              Boolean(pendingDrop?.conflict) ||
              (nearTermWarning && !nearTermConfirmed) ||
              (pendingPhase === 'operation' && (!rescheduleError || !pendingOperationId))
            }
          >
            {pendingPhase === 'operation' ? 'Check status' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

type AdminOperationTrigger = ReturnType<typeof useLazyGetAdminAppointmentOperationQuery>[0];

async function awaitAdminOperation(operationId: string, getOperation: AdminOperationTrigger) {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const operation = await getOperation(operationId, false).unwrap();
    if (operation.status !== 'pending') return operation;
    await new Promise((resolve) =>
      setTimeout(resolve, Math.max(250, (operation.retry_after_seconds ?? 1) * 1000))
    );
  }
  throw new Error('The appointment reschedule is still pending. Refresh to check its status.');
}

function isDraggableNativeAppointment(event: CalendarEvent) {
  return (
    nativeAppointmentId(event) !== null &&
    event.meta?.status === 'scheduled' &&
    event.meta?.bookingStatus === 'confirmed'
  );
}

function nativeAppointmentId(event: CalendarEvent | undefined): string | null {
  const appointmentId = event?.meta?.appointmentId;
  return typeof appointmentId === 'string' && appointmentId.length > 0 ? appointmentId : null;
}

function applyPendingDrop(
  events: CalendarEvent[],
  proposal: DropProposal,
  phase: 'confirmation' | 'operation'
) {
  return events.map((event) =>
    event.id === proposal.eventId
      ? {
          ...event,
          date: proposal.toDate,
          start: proposal.toStart,
          end: proposal.toEnd,
          title: `${phase === 'confirmation' ? 'Pending confirmation' : 'Reschedule pending'} · ${event.title ?? 'Appointment'}`,
        }
      : event
  );
}

function formatDropTime(proposal: DropProposal, timezone: string | undefined) {
  if (!timezone) return proposal.toDate;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(calendarLocalTimeToInstant(proposal.toDate, proposal.toStart, timezone)));
}

function viewRange(
  view: CalendarView,
  cursor: ISODate,
  weekStartsOn: 0 | 1
): { start: ISODate; end: ISODate } {
  if (view === 'month') {
    const dates = monthGridDates(cursor, weekStartsOn);
    return { start: dates[0], end: addDays(dates[dates.length - 1], 1) };
  }

  // Week (and schedule, whose query is skipped in favor of incremental paging).
  const start = mondayOf(cursor, weekStartsOn);
  return { start, end: addDays(start, 7) };
}

function warningKey(warning: {
  code: string;
  connection_id?: string;
  provider_calendar_id?: string;
}) {
  return [warning.code, warning.connection_id, warning.provider_calendar_id]
    .filter(Boolean)
    .join(':');
}

export default AdminCalendarView;

function isTargetNotFoundError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  if (error.status === 403 || error.status === 404) {
    return true;
  }
  if (error.status !== 'PARSING_ERROR' || !('originalStatus' in error)) {
    return false;
  }
  return error.originalStatus === 403 || error.originalStatus === 404;
}
